import React, { useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { useLang } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api } from "@/lib/api";
import WidgetChipsEditor from "@/components/WidgetChipsEditor";
import ServiceAdvisorsEditor from "@/components/ServiceAdvisorsEditor";
import MessageTemplatesEditor from "@/components/MessageTemplatesEditor";
import CSATEditor from "@/components/CSATEditor";
import TeamsEditor from "@/components/TeamsEditor";
import { ReassignSettings } from "@/components/ReassignSettings";
import { EscalationSettings } from "@/components/EscalationSettings";
import { ConnectionLog } from "@/components/ConnectionLog";
import Integrations from "@/pages/Integrations";
import KnowledgeBase from "@/pages/KnowledgeBase";
import BotHealthPanel from "@/components/BotHealthPanel";
import BotTrainingPanel from "@/components/BotTrainingPanel";
import UsersEditor from "@/components/UsersEditor";
import {
  WhatsappLogo, FacebookLogo, InstagramLogo,
  Lightning, Robot, Check, CircleNotch, ChatCircleDots,
  IdentificationBadge, FloppyDisk, Star, Palette, UsersFour, Plug, IdentificationCard,
  BookOpen, Heartbeat, GraduationCap, FilePdf, ClockCounterClockwise,
} from "@phosphor-icons/react";
import DocsSection from "@/components/DocsSection";

const INTEGRATIONS = [
  { name: "WhatsApp Business", icon: WhatsappLogo, color: "text-emerald-500", status: "mock" },
  { name: "Facebook Messenger", icon: FacebookLogo, color: "text-blue-500", status: "mock" },
  { name: "Instagram Business", icon: InstagramLogo, color: "text-pink-500", status: "mock" },
  { name: "Zapier CRM", icon: Lightning, color: "text-orange-500", status: "mock" },
];

export default function Settings() {
  const { t, lang, setLang } = useLang();
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isManager = isAdmin || user?.role === "supervisor";
  const canEditCsat = isManager;
  const [modelState, setModelState] = useState({ active: "claude-sonnet-4-6", options: [] });
  const [savingModel, setSavingModel] = useState(null);
  const [section, setSection] = useState("preferences");

  const SECTIONS = [
    { id: "preferences", label: lang === "es" ? "Preferencias" : "Preferences", icon: Palette, admin: false },
    { id: "ai", label: lang === "es" ? "IA & Concierge" : "AI & Concierge", icon: Robot, admin: true },
    { id: "training", label: lang === "es" ? "Entrenamiento del bot" : "Bot Training", icon: GraduationCap, manager: true },
    { id: "health", label: lang === "es" ? "Salud del bot" : "Bot Health", icon: Heartbeat, admin: false },
    { id: "templates", label: lang === "es" ? "Plantillas" : "Templates", icon: Lightning, admin: false },
    { id: "advisors", label: lang === "es" ? "Asesores Motos" : "Motorcycle Advisors", icon: IdentificationBadge, admin: false },
    { id: "csat", label: "CSAT", icon: Star, admin: false },
    { id: "teams", label: lang === "es" ? "Equipos" : "Teams", icon: UsersFour, admin: false },
    { id: "users", label: lang === "es" ? "Usuarios" : "Users", icon: IdentificationCard, manager: true },
    { id: "integrations", label: lang === "es" ? "Integraciones" : "Integrations", icon: Plug, admin: false },
    { id: "knowledge", label: lang === "es" ? "Base de Conocimiento" : "Knowledge Base", icon: BookOpen, admin: false },
    { id: "docs", label: lang === "es" ? "Documentación" : "Documentation", icon: FilePdf, admin: true },
    { id: "connections", label: lang === "es" ? "Registro de conexiones" : "Connection log", icon: ClockCounterClockwise, admin: true },
  ];
  const visibleSections = SECTIONS.filter((s) => (!s.admin || isAdmin) && (!s.manager || isManager));

  useEffect(() => {
    let alive = true;
    api.get("/settings/concierge-model")
      .then((r) => { if (alive) setModelState(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  async function changeModel(value) {
    if (value === modelState.active) return;
    setSavingModel(value);
    try {
      const { data } = await api.put("/settings/concierge-model", { model: value });
      setModelState((s) => ({ ...s, active: data.active }));
      const label = modelState.options.find((o) => o.value === value)?.label || value;
      toast.success(lang === "es" ? `Modelo cambiado a ${label}` : `Model switched to ${label}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error";
      toast.error(msg);
    } finally {
      setSavingModel(null);
    }
  }

  return (
    <>
      <Topbar title={t("set.title")} subtitle={t("set.subtitle")} />
      <div className="flex-1 min-h-0 flex overflow-hidden animate-fade-in">
        {/* Vertical sidebar */}
        <aside className="w-56 shrink-0 border-r border-border bg-card/50 overflow-y-auto py-4" data-testid="settings-sidebar">
          <nav className="space-y-0.5 px-2">
            {visibleSections.map((s) => {
              const Icon = s.icon;
              const active = section === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setSection(s.id)}
                  data-testid={`settings-nav-${s.id}`}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs rounded-md transition-colors ${
                    active ? "bg-primary text-primary-foreground font-medium" : "hover:bg-muted text-muted-foreground"
                  }`}
                >
                  <Icon size={13} weight={active ? "fill" : "regular"} />
                  <span className="truncate">{s.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Right content */}
        <div className="flex-1 min-w-0 overflow-y-auto p-6 lg:p-8 space-y-6">
        {section === "preferences" && (
        <div className="border border-border rounded-md p-6 bg-card" data-testid="section-preferences">
          <div className="overline mb-4">{t("set.theme")} & {t("set.language")}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-sm font-medium mb-2">{t("set.theme")}</div>
              <div className="flex gap-2">
                <Button variant={theme === "light" ? "default" : "outline"} size="sm" onClick={() => setTheme("light")} data-testid="set-light">Light</Button>
                <Button variant={theme === "dark" ? "default" : "outline"} size="sm" onClick={() => setTheme("dark")} data-testid="set-dark">Dark</Button>
              </div>
            </div>
            <div>
              <div className="text-sm font-medium mb-2">{t("set.language")}</div>
              <div className="flex gap-2">
                <Button variant={lang === "es" ? "default" : "outline"} size="sm" onClick={() => setLang("es")} data-testid="set-es">Español</Button>
                <Button variant={lang === "en" ? "default" : "outline"} size="sm" onClick={() => setLang("en")} data-testid="set-en">English</Button>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* AI — Advanced settings visible only to admin */}
        {isAdmin && section === "ai" && (
        <div className="border border-border rounded-md p-6 bg-card" data-testid="settings-advanced-ai">
          <div className="overline mb-4 flex items-center gap-1.5">
            <Robot size={12}/> {t("set.ai")}
            <Badge variant="outline" className="ml-1 text-[9px] uppercase border-amber-500/40 text-amber-600">
              {lang === "es" ? "Solo Admin" : "Admin only"}
            </Badge>
          </div>

          {/* Concierge Model selector */}
          <div className="mb-6">
            <div className="text-sm font-medium mb-1">{lang === "es" ? "Modelo del Concierge" : "Concierge model"}</div>
            <div className="text-xs text-muted-foreground mb-3">
              {lang === "es"
                ? "El modelo que responde en el widget web y en WhatsApp/Instagram. Cambia el tono y velocidad."
                : "Model powering the web widget and WhatsApp/Instagram replies. Changes tone and latency."}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="concierge-model-selector">
              {modelState.options.map((opt) => {
                const active = opt.value === modelState.active;
                const saving = savingModel === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => changeModel(opt.value)}
                    disabled={saving || savingModel !== null}
                    data-testid={`concierge-model-${opt.value}`}
                    className={`text-left rounded-md border p-3 transition-all ${
                      active
                        ? "border-primary bg-primary/5 ring-1 ring-primary/40"
                        : "border-border hover:border-primary/40 hover:bg-muted/40"
                    } ${savingModel !== null && !saving ? "opacity-50" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-medium leading-tight flex items-center gap-1.5">
                          {opt.label}
                          {opt.recommended && (
                            <Badge variant="outline" className="text-[9px] uppercase border-emerald-500/40 text-emerald-600">
                              {lang === "es" ? "Recomendado" : "Recommended"}
                            </Badge>
                          )}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">{opt.provider}</div>
                      </div>
                      {saving ? (
                        <CircleNotch size={16} className="animate-spin text-primary" />
                      ) : active ? (
                        <Check size={16} weight="bold" className="text-primary" />
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Auto-scoring</div>
                <div className="text-xs text-muted-foreground">Puntuación automática al crear leads</div>
              </div>
              <Switch defaultChecked data-testid="set-autoscore"/>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">RAG</div>
                <div className="text-xs text-muted-foreground">Retrieval Augmented Generation activo</div>
              </div>
              <Switch defaultChecked data-testid="set-rag"/>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Sentiment analysis</div>
                <div className="text-xs text-muted-foreground">Análisis de sentimiento</div>
              </div>
              <Switch defaultChecked data-testid="set-sent"/>
            </div>
          </div>
        </div>
        )}

        {isManager && section === "training" && (
          <div data-testid="section-training"><BotTrainingPanel /></div>
        )}

        {isAdmin && section === "docs" && <DocsSection />}
        {isAdmin && section === "connections" && <ConnectionLog />}

        {/* Bot Health */}
        {section === "health" && (
        <div data-testid="section-health">
          <BotHealthPanel lang={lang} />
        </div>
        )}

        {/* Message Templates */}
        {section === "templates" && (
        <div className="border border-border rounded-md p-6 bg-card" data-testid="section-templates">
          <div className="overline mb-4 flex items-center gap-1.5">
            <Lightning size={12}/> {lang === "es" ? "Plantillas de mensajes rápidos" : "Quick-reply templates"}
          </div>
          <MessageTemplatesEditor lang={lang} />
        </div>
        )}

        {/* Service Advisors CRUD */}
        {section === "advisors" && (
        <div className="border border-border rounded-md p-6 bg-card" data-testid="section-advisors">
          <div className="overline mb-4 flex items-center gap-1.5">
            <IdentificationBadge size={12}/> {lang === "es" ? "Asesores Motos" : "Motorcycle Advisors"}
          </div>
          <ServiceAdvisorsEditor lang={lang} />
        </div>
        )}

        {/* Closing message + CSAT under 'csat' section */}
        {section === "csat" && (
        <>
          <ClosingMessageEditor lang={lang} />
          <div className="border border-border rounded-md p-6 bg-card" data-testid="csat-editor-section">
            <div className="overline mb-4 flex items-center gap-1.5">
              <Star size={12} weight="fill" className="text-amber-500" />
              {lang === "es" ? "Encuesta CSAT (Satisfacción)" : "CSAT Survey (Satisfaction)"}
              <Badge variant="outline" className="ml-1 text-[9px] uppercase border-amber-500/40 text-amber-600">
                {lang === "es" ? "Admin y Supervisor" : "Admin & Supervisor"}
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground mb-4">
              {lang === "es"
                ? "Edita las preguntas CSAT bilingües (ES/EN) que se muestran al cliente cuando la conversación se cierra. Escala 1–5 estrellas."
                : "Edit the bilingual (ES/EN) CSAT questions shown to the customer when a conversation is resolved. 1–5 star scale."}
            </div>
            <CSATEditor lang={lang} canEdit={canEditCsat} />
          </div>
        </>
        )}

        {/* Teams (BDC routing) */}
        {section === "teams" && (
        <div className="border border-border rounded-md p-6 bg-card" data-testid="section-teams">
          <div className="overline mb-4 flex items-center gap-1.5">
            <UsersFour size={12}/> {lang === "es" ? "Equipos BDC y routing" : "BDC Teams and routing"}
          </div>
          <ReassignSettings canEdit={isAdmin} />
          <EscalationSettings />
          <TeamsEditor lang={lang} />
        </div>
        )}

        {/* Users (admin-only CRUD) */}
        {isManager && section === "users" && (
        <div className="border border-border rounded-md p-6 bg-card" data-testid="section-users">
          <div className="overline mb-4 flex items-center gap-1.5">
            <IdentificationCard size={12}/> {lang === "es" ? "Usuarios · gestión de cuentas" : "Users · account management"}
          </div>
          <UsersEditor lang={lang} />
        </div>
        )}

        {/* Integrations */}
        {section === "integrations" && (
        <div data-testid="section-integrations">
          <Integrations embedded />
        </div>
        )}

        {/* Knowledge Base */}
        {section === "knowledge" && (
        <div className="border border-border rounded-md p-6 bg-card" data-testid="section-knowledge">
          <div className="overline mb-4 flex items-center gap-1.5">
            <BookOpen size={12}/> {lang === "es" ? "Base de Conocimiento" : "Knowledge Base"}
          </div>
          <KnowledgeBase embedded />
        </div>
        )}

        </div>
      </div>
    </>
  );
}

// Configurable "closing phrase" appended to every closed/escalated conversation.
function ClosingMessageEditor({ lang }) {
  const [msg, setMsg] = React.useState("");
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    api.get("/settings/closing-message")
      .then((r) => { setMsg(r.data.value || ""); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);
  async function save() {
    setSaving(true);
    try {
      await api.put("/settings/closing-message", { value: msg });
      toast.success(lang === "es" ? "Frase de cierre guardada" : "Closing phrase saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="border border-border rounded-md p-6 bg-card">
      <div className="overline mb-3 flex items-center gap-1.5">
        <ChatCircleDots size={12}/> {lang === "es" ? "Frase de cierre" : "Closing phrase"}
      </div>
      <div className="text-xs text-muted-foreground mb-2">
        {lang === "es"
          ? "El bot añade esta frase al cerrar o escalar una conversación (después del último mensaje)."
          : "The bot appends this phrase after closing or escalating a conversation."}
      </div>
      <div className="flex gap-2">
        <textarea
          rows={2}
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          disabled={!loaded || saving}
          placeholder={lang === "es"
            ? "Ej: Gracias por contactar a SDM. ¿Qué más puedo hacer por ti?"
            : "e.g. Thanks for reaching out to SDM. What else can I help you with?"}
          className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
          data-testid="closing-message-input"
        />
        <Button onClick={save} disabled={!loaded || saving} data-testid="closing-message-save">
          {saving ? <CircleNotch size={12} className="animate-spin mr-1"/> : <FloppyDisk size={12} className="mr-1"/>}
          {lang === "es" ? "Guardar" : "Save"}
        </Button>
      </div>
    </div>
  );
}

// Public shareable /chat link with copy-to-clipboard + QR-friendly display.
function PublicChatLink({ lang }) {
  const publicUrl = `${window.location.origin}/chat`;
  const [copied, setCopied] = React.useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success(lang === "es" ? "Link copiado" : "Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error(lang === "es" ? "No se pudo copiar" : "Copy failed");
    }
  }
  return (
    <div
      className="mb-5 rounded-md border border-primary/30 bg-primary/5 p-4"
      data-testid="public-chat-link"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-md bg-primary/15 text-primary flex items-center justify-center shrink-0">
          <Lightning size={16} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">
            {lang === "es"
              ? "Link público para probar el chat"
              : "Public link to test the chat"}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {lang === "es"
              ? "Compártelo con clientes, colegas o marketing. Sin login. Todas las conversaciones aparecen en la Bandeja Omnicanal."
              : "Share it with customers, colleagues or marketing. No login. All conversations show up in the Inbox."}
          </div>
          <div className="mt-2 flex flex-col sm:flex-row gap-2">
            <input
              readOnly
              value={publicUrl}
              className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-xs font-mono truncate"
              onFocus={(e) => e.target.select()}
              data-testid="public-chat-url"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={copy}
                data-testid="public-chat-copy"
              >
                {copied
                  ? (lang === "es" ? "¡Copiado!" : "Copied!")
                  : (lang === "es" ? "Copiar link" : "Copy link")}
              </Button>
              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="public-chat-open"
              >
                <Button size="sm">
                  {lang === "es" ? "Abrir" : "Open"}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

